import { useRef, useState, useCallback } from 'react';
import { Trans, t } from '@lingui/macro';
import _ from 'lodash';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    makeStyles,
} from '@material-ui/core';

import { MaterialUiIcon } from '../../icon';
import Link from '../../links/Link';
import { AdminPages, buildRootPageTarget } from '../AdminLinkUtils';
import { useDataService } from '../../../services/ApiDataService';

const MAX_FILE_SIZE = 20 * 1024 * 1024; // ~20MB

const POLL_INTERVAL = 500;

const useStyles = makeStyles((theme) => ({
    dialogPaper: {
        width: 400,
    },
    browseRow: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(1),
    },
    browseButton: {
        padding: theme.spacing(2),
    },
    fileNameField: {
        flexGrow: 1,
        minHeight: 26,
        borderBottom: `1px dashed ${theme.palette.text.disabled}`,
        color: theme.palette.text.secondary,
    },
    fileNameText: {
        width: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    uploadFeedback: {
        maxHeight: 400,
        overflow: 'auto',
    },
    importMessageRow: {
        display: 'flex',
        alignItems: 'flex-start',
    },
    importMessageContent: {
        display: 'flex',
        alignItems: 'flex-start',
        minWidth: 0,
    },
    importedItemLink: {
        display: 'block',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
    },
    importErrors: {
        margin: theme.spacing(1.25),
    },
}));

function buildEditUrl(testTypeName) {
    const baseUrl = buildRootPageTarget(AdminPages.ServiceTests);
    const encoded = encodeURIComponent(testTypeName || '');
    return encoded ? `${baseUrl}/${encoded}` : baseUrl;
}

export default function TestTypeImportDialog({ open, onClose, onRefresh }) {
    const classes = useStyles();
    const [file, setFile] = useState(null);
    const [fileSizeError, setFileSizeError] = useState(false);

    const [uploaded, setUploaded] = useState(false);
    const [importing, setImporting] = useState(false);
    const [uploadStatus, setUploadStatus] = useState('');
    const [uploadMessage, setUploadMessage] = useState('');
    const [importMessage, setImportMessage] = useState('');
    const [importItems, setImportItems] = useState([]);
    const [importErrors, setImportErrors] = useState([]);

    const timerRef = useRef(null);
    const dataService = useDataService();

    const reset = useCallback(() => {
        setFile(null);
        setFileSizeError(false);
        setUploaded(false);
        setImporting(false);
        setUploadStatus('');
        setUploadMessage('');
        setImportMessage('');
        setImportItems([]);
        setImportErrors([]);
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const handleFileChange = async (e) => {
        setFileSizeError(false);
        const jsonFile = e.target.files?.[0];
        if (!jsonFile) return;

        setFile(jsonFile);
        await handleUpload(jsonFile);
    };

    const pollImportStatus = useCallback(
        (importId) => {
            timerRef.current = setInterval(async () => {
                try {
                    const data = await dataService.get('/ipm/v1/admin/testTypesImport/importStatus', { id: importId });
                    const status = data?.status ?? '';
                    const message = data?.message ?? '';
                    const items = data?.items ?? [];

                    setImportMessage(message);
                    setImportItems(items);

                    if (status === 'partialSuccess' || status === 'failed') {
                        if (_.isArray(data.errorLog)) {
                            setImportErrors((prev) => [
                                ...prev,
                                ...data.errorLog.map((msg) => ({ id: _.uniqueId('importErr_'), message: msg })),
                            ]);
                        }
                    }

                    if (status !== 'processing') {
                        setImporting(false);
                        clearInterval(timerRef.current);
                        timerRef.current = null;
                    }
                } catch {
                    setImporting(false);
                    clearInterval(timerRef.current);
                    timerRef.current = null;
                }
            }, POLL_INTERVAL);
        },
        [dataService]
    );

    const handleUpload = async (selectedFile = file) => {
        if (!selectedFile) return;

        if (selectedFile.size > MAX_FILE_SIZE) {
            setFileSizeError(true);
            return;
        }

        setUploaded(true);
        setImporting(true);
        setImportMessage(t`Import in progress`);
        setImportItems([]);
        setImportErrors([]);

        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const data = await dataService.postFormData('/ipm/v1/admin/testTypesImport/importJSON', formData);
            const status = data?.status ?? '';
            const message = data?.message ?? '';
            setUploadStatus(status);
            setUploadMessage(message);

            if (status === 'failed') {
                setImporting(false);
            } else {
                const importId = data?.id;
                if (importId != null) {
                    pollImportStatus(importId);
                }
            }
            onRefresh();
        } catch {
            setUploadStatus('failed');
            setUploadMessage(t`Upload failed`);
            setImporting(false);
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} classes={{ paper: classes.dialogPaper }}>
            <DialogTitle>
                <Trans>Import File</Trans>
            </DialogTitle>
            <DialogContent>
                {!uploaded && (
                    <>
                        <Box className={classes.browseRow}>
                            <Button
                                className={classes.browseButton}
                                component="label"
                                color="primary"
                                startIcon={<MaterialUiIcon icon="file_upload" />}
                            >
                                {t`Browse`}
                                <input
                                    type="file"
                                    accept=".json"
                                    style={{ display: 'none' }}
                                    onChange={handleFileChange}
                                />
                            </Button>
                            <div className={classes.fileNameField}>
                                <Typography variant="body2" className={classes.fileNameText}>
                                    {file?.name || ''}
                                </Typography>
                            </div>
                        </Box>
                        <Box mt={1}>
                            <Typography variant="body2">
                                <Trans>Select a JSON file.</Trans>
                            </Typography>
                            {fileSizeError && (
                                <Typography variant="body2" style={{ color: '#E66026' }}>
                                    <Trans>Selected file is too large, file up to 20MB is supported.</Trans>
                                </Typography>
                            )}
                        </Box>
                    </>
                )}

                {uploaded && (
                    <Box className={classes.uploadFeedback}>
                        {uploadStatus === 'failed' && (
                            <Typography variant="body2" color="error">
                                {uploadMessage}
                            </Typography>
                        )}

                        <Box>
                            <Box className={classes.importMessageRow}>
                                {importing && <CircularProgress size={16} style={{ marginRight: 10 }} />}
                                <div className={classes.importMessageContent}>
                                    <div>{`${importMessage}: `}</div>
                                    {importItems.length === 1 && (
                                        <Link
                                            to={buildEditUrl(importItems[0].name)}
                                            className={classes.importedItemLink}
                                            title={t`Edit Test Type`}
                                            onClick={handleClose}
                                        >
                                            {importItems[0].title} ({importItems[0].message})
                                        </Link>
                                    )}
                                </div>
                            </Box>
                        </Box>

                        {importItems.length > 1 && (
                            <Box mt={1}>
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    {importItems.map((item) => (
                                        <li key={item.name}>
                                            <Link to={buildEditUrl(item.name)} title={t`Edit`} onClick={handleClose}>
                                                {item.title} ({item.message})
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </Box>
                        )}

                        {importErrors.length > 0 && (
                            <Box className={classes.importErrors}>
                                {importErrors.map((err) => (
                                    <Typography key={err.id} variant="body2" color="error">
                                        {err.message}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {!uploaded && (
                    <Button onClick={handleUpload} disabled={!file || importing}>
                        <Trans>Upload</Trans>
                    </Button>
                )}
                <Button onClick={handleClose} disabled={importing}>
                    <Trans>Close</Trans>
                </Button>
            </DialogActions>
        </Dialog>
    );
}
